import React, { useEffect, useMemo, useState } from "react";
import { userService } from "../services/userService";
import GloablButton from "../components/GloablButton";
import vendorOrderController from "../controllers/vendorOrderController";
import vendorStorefrontController from "../controllers/vendorStorefrontController";
import VendorLayout from "./VendorLayout";
import { getLanguage, translateLabel, useTranslationSnapshot } from "./../translations/runtime";


const ORDER_TABS = [
  { key: "all", label: "All", labelId: "input_all", status: "" },
  { key: "request", label: "Request", labelId: "txt_web_vendor_request", status: 0 },
  { key: "preparing", label: "Preparing", labelId: "txt_common_preparing", status: 1 },
  { key: "completed", label: "Completed", labelId: "txt_common_completed", status: 2 },
];

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? response;

const isApiError = (response) => response?.isAxiosError || response?.response;

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;

  return [];
};

const STATUS_LABELS = {
  0: "Pending",
  1: "Preparing",
  2: "Completed",
};

const STATUS_LABEL_IDS = {
  0: "txt_common_pending", 1: "txt_common_preparing", 2: "txt_common_completed",
};

const toMoney = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "$0";

  return `$${numericValue.toFixed(2).replace(/\.00$/, "")}`;
};

const toDateFromApiObject = (value) => {
  if (!value) return null;
  if (value.time) return new Date(value.time);
  if (typeof value === "string") return new Date(value);

  return null;
};

const toTimeAgo = (value) => {
  const date = toDateFromApiObject(value);
  if (!date || Number.isNaN(date.getTime())) return "";

  const diffMinutes = Math.max(1, Math.round((Date.now() - date.getTime()) / 60000));
  if (diffMinutes < 60) return translateLabel("txt_web_vendor_minutes_ago", "{count} min ago", { count: diffMinutes });

  return translateLabel("txt_web_vendor_hours_ago", "{count} hr ago", { count: Math.round(diffMinutes / 60) });
};

const toDateLabel = (value) => {
  const date = toDateFromApiObject(value);
  if (!date || Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString(getLanguage() === "ar" ? "ar-LB-u-ca-gregory" : "en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const mapOrder = (order) => ({
  id: order.orderId || order.id || "",
  customer: order.customerName || "",
  phone: order.customerPhone || "",
  status: Number(order.status ?? 0),
  total: toMoney(order.totalAmount),
  items: Array.isArray(order.items)
    ? order.items.map((item) => ({
        name: item.itemName || "",
        quantity: item.quantity ? `x${item.quantity}` : "",
      }))
    : [],
  createdAt: order.createdAt,
});

const toCounts = (payload) => ({
  all: payload?.all ?? 0,
  completed: payload?.completed ?? 0,
  preparing: payload?.preparing ?? 0,
  request: payload?.request ?? 0,
});

const fetchOrderData = async () => {
    const companyId = userService.getUserData().Compid;
  const [ordersResponse, countsResponse] = await Promise.all([
    vendorOrderController.list({
      Authorization: userService.getJwt(),
      companyId,
    }),
    vendorOrderController.counts({
      Authorization: userService.getJwt(),
      companyId,
    }),
  ]);

  return {
    counts: isApiError(countsResponse)
      ? null
      : toCounts(unwrapPayload(countsResponse)),
    orders: isApiError(ordersResponse)
      ? []
      : getRows(unwrapPayload(ordersResponse)).map(mapOrder),
  };
};

const getStatusClass = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");

function VendorOrders() {
  useTranslationSnapshot();
  const [activeTab, setActiveTab] = useState("all");
  const [orders, setOrders] = useState([]);
  const [orderCounts, setOrderCounts] = useState({
    all: 0,
    completed: 0,
    preparing: 0,
    request: 0,
  });
  const [newOrdersPaused, setNewOrdersPaused] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [isPauseUpdating, setIsPauseUpdating] = useState(false);
  const [actingOrderId, setActingOrderId] = useState("");

  const tabCounts = useMemo(
    () =>
      ORDER_TABS.reduce((counts, tab) => {
        counts[tab.key] = orderCounts[tab.key] ?? 0;

        return counts;
      }, {}),
    [orderCounts],
  );

  const visibleOrders = useMemo(() => {
    const activeStatus = ORDER_TABS.find((tab) => tab.key === activeTab)?.status;

    return activeStatus !== ""
      ? orders.filter((order) => order.status === activeStatus)
      : orders;
  }, [activeTab, orders]);

  useEffect(() => {
    let isMounted = true;

    const loadOrders = async () => {
      setIsOrdersLoading(true);

      try {
        const nextData = await fetchOrderData();

        if (!isMounted) return;

        setOrders(nextData.orders);
        if (nextData.counts) {
          setOrderCounts(nextData.counts);
        }
      } catch {
        if (!isMounted) return;
        setOrders([]);
      } finally {
        if (isMounted) {
          setIsOrdersLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      isMounted = false;
    };
  }, []);

  const refreshOrders = async () => {
    const nextData = await fetchOrderData();

    setOrders(nextData.orders);
    if (nextData.counts) {
      setOrderCounts(nextData.counts);
    }
  };

  const runOrderAction = async (orderId, action) => {
    const actionKey = `${action}:${orderId}`;

    setActingOrderId(actionKey);

    try {
            const companyId = userService.getUserData().Compid;
      let response = null;

      if (action === "accept") {
        response = await vendorOrderController.accept({
          Authorization: userService.getJwt(),
          companyId,
          orderId,
          request: {
            preparationTimeMinutes: 20,
            reason: "",
          },
        });
      }

      if (action === "reject") {
        response = await vendorOrderController.reject({
          Authorization: userService.getJwt(),
          companyId,
          orderId,
          request: {
            preparationTimeMinutes: 0,
            reason: "Rejected by vendor",
          },
        });
      }

      if (action === "complete") {
        response = await vendorOrderController.complete({
          Authorization: userService.getJwt(),
          companyId,
          orderId,
        });
      }

      if (!isApiError(response)) {
        await refreshOrders();
      }
    } finally {
      setActingOrderId("");
    }
  };

  const togglePauseNewOrders = async () => {
    if (isPauseUpdating) return;

    const nextPaused = !newOrdersPaused;
    setIsPauseUpdating(true);

    try {
            const companyId = userService.getUserData().Compid;
      const response = await vendorStorefrontController.pauseNewOrders({
        Authorization: userService.getJwt(),
        companyId,
        paused: nextPaused,
      });

      if (!isApiError(response)) {
        const payload = unwrapPayload(response);
        setNewOrdersPaused(
          typeof payload?.newOrdersPaused === "boolean"
            ? payload.newOrdersPaused
            : nextPaused,
        );
      }
    } finally {
      setIsPauseUpdating(false);
    }
  };

  return (
    <VendorLayout activeKey="orders">
      <section className="vendor-orders-page">
        <div className="vendor-orders-header">
          <div className="vendor-menu-heading">
            <div>
              <div className="vendor-orders-page-title">{translateLabel("txt_web_vendor_order_management", "Order Management")}</div>
              <div className="vendor-orders-page-subtitle">
                {translateLabel("txt_web_vendor_manage_active_orders", "Manage active orders in real-time")}
              </div>
            </div>
          </div>

          <div className="vendor-store-online">
            <div aria-hidden="true" /> {translateLabel("txt_store_online", "Store Online")} </div>
        </div>

        <div className="vendor-orders-top-row">
          <div className="vendor-orders-tabs" role="tablist">
            {ORDER_TABS.map((tab) => (
              <GloablButton
                className={`vendor-orders-tab ${
                  activeTab === tab.key ? "active" : ""
                }`}
                width="100%"
                height="25px"
                background={activeTab === tab.key ? "#fa9c03" : "#2b1f1a"}
                borderRadius="6px"
                fontSize="10px"
                fontWeight={700}
                lineHeight="14px"
                padding="0 8px"
                text={`${translateLabel(tab.labelId, tab.label)} (${tabCounts[tab.key] || 0})`}
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
              />
            ))}
          </div>

          <GloablButton
            className="vendor-orders-pause-button"
            width="155px"
            height="25px"
            background={newOrdersPaused ? "#dcfce7" : "#ffe0e0"}
            borderRadius="5px"
            color={newOrdersPaused ? "#166534" : "#ff0000"}
            fontSize="10px"
            fontWeight={700}
            lineHeight="13px"
            padding="0 12px"
            text={
              isPauseUpdating
                ? translateLabel("txt_end_user_updating", "Updating...")
                : newOrdersPaused
                  ? translateLabel("txt_web_vendor_resume_new_orders", "Resume New Orders")
                  : translateLabel("btn_pause_new_orders", "Pause New Orders")
            }
            disabled={isPauseUpdating}
            onClick={togglePauseNewOrders}
          />
        </div>

        <div className="vendor-orders-list">
          {isOrdersLoading && (
            <div className="vendor-orders-empty">{translateLabel("txt_web_vendor_loading_orders", "Loading orders...")}</div>
          )}

          {visibleOrders.map((order) => (
            <article className="vendor-order-card" key={order.id}>
              <div className="vendor-order-card-main">
                <div className="vendor-order-card-head">
                  <div className="vendor-order-id-row">
                    <div className="vendor-order-id">{order.id}</div>
                    <div
                      className={`vendor-order-status vendor-order-status-${getStatusClass(
                        STATUS_LABELS[order.status],
                      )}`}
                    >
                      {translateLabel(STATUS_LABEL_IDS[order.status] || "txt_common_pending", STATUS_LABELS[order.status] || "Pending")}
                    </div>
                  </div>

                  <div className="vendor-order-total">
                    <span>{translateLabel("txt_end_user_total", "Total")}</span>
                    <strong>{order.total}</strong>
                  </div>
                </div>

                <div className="vendor-order-customer">
                  <div>{order.customer || translateLabel("txt_web_vendor_unknown_customer", "Unknown customer")}</div>
                  <span>{order.phone}</span>
                </div>

                <div className="vendor-order-items">
                  <div>{translateLabel("txt_web_vendor_order_items_count", "Items ({count})", { count: order.items.length })}</div>
                  {order.items.map((item, index) => {
                    const label = [item.name || translateLabel("txt_end_user_item", "Item"), item.quantity].filter(Boolean).join(" ");
                    return <span key={index}>* {label}</span>;
                  })}
                </div>

                <div className="vendor-order-meta">
                  <span>{toTimeAgo(order.createdAt)}</span>
                  <span>{toDateLabel(order.createdAt)}</span>
                </div>

                {order.status === 0 && (
                  <div className="vendor-order-actions two">
                    <GloablButton
                      className="vendor-order-action"
                      width="100%"
                      height="28px"
                      background="#ff8a2a"
                      borderRadius="4px"
                      fontSize="9px"
                      fontWeight={600}
                      lineHeight="13px"
                      padding="0 12px"
                      text={
                        actingOrderId === `accept:${order.id}`
                          ? translateLabel("txt_web_vendor_accepting", "Accepting...")
                          : translateLabel("txt_web_vendor_accept_order", "Accept Order")
                      }
                      disabled={Boolean(actingOrderId)}
                      onClick={() => runOrderAction(order.id, "accept")}
                    />
                    <GloablButton
                      className="vendor-order-action"
                      width="100%"
                      height="28px"
                      background="#ffffff"
                      border="1px solid #e5e7eb"
                      borderRadius="4px"
                      color="#1d2939"
                      fontSize="9px"
                      fontWeight={600}
                      lineHeight="13px"
                      padding="0 12px"
                      text={
                        actingOrderId === `reject:${order.id}`
                          ? translateLabel("txt_web_vendor_declining", "Declining...")
                          : translateLabel("txt_web_vendor_decline", "Decline")
                      }
                      disabled={Boolean(actingOrderId)}
                      onClick={() => runOrderAction(order.id, "reject")}
                    />
                  </div>
                )}

                {order.status === 1 && (
                  <div className="vendor-order-actions">
                    <GloablButton
                      className="vendor-order-action"
                      width="100%"
                      height="28px"
                      background="#ff8a2a"
                      borderRadius="4px"
                      fontSize="9px"
                      fontWeight={600}
                      lineHeight="13px"
                      padding="0 12px"
                      text={
                        actingOrderId === `complete:${order.id}`
                          ? translateLabel("txt_web_vendor_completing", "Completing...")
                          : translateLabel("txt_web_vendor_mark_as_completed", "Mark as Completed")
                      }
                      disabled={Boolean(actingOrderId)}
                      onClick={() => runOrderAction(order.id, "complete")}
                    />
                  </div>
                )}
              </div>
            </article>
          ))}

          {!isOrdersLoading && visibleOrders.length === 0 && (
            <div className="vendor-orders-empty">{translateLabel("txt_web_vendor_no_orders_found", "No orders found.")}</div>
          )}
        </div>
      </section>
    </VendorLayout>
  );
}

export default VendorOrders;
